/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    HistoryItem,
    HistoryOfferItem,
    HistoryOfferItemShortReasonEnum,
} from 'client-code/recordings';

import { ContentType, InteractiveComponents } from '../../../core/enums';
import { ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation';
import { NetworkManager } from '../../../core/network/NetworkManager';
import type { TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { TOfferPreview, TOfferPreviewCustomData } from '../../../core/types';
import { UIOrchestrator, UIOrchestratorState } from '../../../core/uiorchestrator';
import { ContentScreenPresentationId } from '../../../views/ProgramScreen/ContentScreenPresentationId';
import {
    getHistoryItemsFromPage,
    getRecordingOfferItemTitle,
    LIST_ITEMS_MAX_COUNT,
    LIST_ROWS_ON_PAGE,
    mapHistoryItemToMessageKey,
} from '../../../views/SettingsScreen/Manage/RecordingActivity/RecordingActivity.utils';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import { OverlayTypes } from '../../overlay/OverlayTypes';
import { showPCPinOverlayWithProgramRating } from '../../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { programScreenTransition } from '../../screens/navigation/screensnavigation';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';
import {
    ON_KEY_DOWN_RECORDING_ACTIVITY,
    ON_KEY_UP_RECORDING_ACTIVITY,
} from './navigation/recordingactivity.navigation.types';
import {
    setCaretVisibilityAction,
    setCurrentPageRecordingActivityAction,
    setGhostModeAction,
    setRecordingActivityApiErrorAction,
    setRecordingActivityDataAction,
    setRecordingActivitySelectedRowPreviewAction,
    setRetryFetchCounterAction,
    setTotalCountRecordingActivityAction,
    setVerticalPositionRecordingActivityAction,
} from './recordingactivity.actions';
import {
    ON_GO_NEXT_PAGE_RECORDING_ACTIVITY,
    ON_GO_PREVIOUS_PAGE_RECORDING_ACTIVITY,
    ON_RETRY_FETCH_RECORDING_ACTIVITY_WHEN_ERROR,
    ON_SET_CARET_VISIBILITY_RECORDING_ACTIVITY,
    ON_SET_CURRENT_PAGE_RECORDING_ACTIVITY,
    ON_SET_GHOST_MODE_RECORDING_ACTIVITY,
    ON_SET_RECORDING_ACTIVITY_API_ERROR,
    ON_SET_RECORDING_ACTIVITY_DATA,
    ON_SET_RECORDING_ACTIVITY_SELECTED_ROW_PREVIEW,
    ON_SET_TOTAL_COUNT_RECORDING_ACTIVITY,
    ON_SET_VERTICAL_POSITION_RECORDING_ACTIVITY,
} from './recordingactivity.types';

interface IResetOptions {
    resetError?: boolean;
    resetCounter?: boolean;
}

interface IRecordingActivityState {
    recordingItems: HistoryItem[];
    verticalPosition: number;
    currentPage: number;
    totalCount: number;
    selectedRecordingActivityPreviewPromise: Promise<TOfferPreview> | null;
    ghostMode: boolean;
    isError: boolean;
    isCaretVisible: boolean;
    retryFetchCounter: number;
}

const initialState: IRecordingActivityState = {
    recordingItems: [],
    verticalPosition: 0,
    currentPage: 0,
    totalCount: 0,
    ghostMode: false,
    isError: false,
    isCaretVisible: false,
    retryFetchCounter: 0,
    selectedRecordingActivityPreviewPromise: null,
};

export const HISTORY_INFO_MOUSE_TRAP = 'hideRecordActivityConflictInfoOverlay';
const TAG = 'RecordingActivityReducers';

const HARD_FINAL_PAGE = Math.ceil(LIST_ITEMS_MAX_COUNT / LIST_ROWS_ON_PAGE) - 1;

// Singleton subscription manager for UIOrchestrator state changes
let uiOrchestratorSubscription: Function | null = null;

const subscribeToUIOrchestrator = (dispatch: Function) => {
    if (uiOrchestratorSubscription) return;

    const callback = (state: UIOrchestratorState) => {
        switch (state) {
            case UIOrchestratorState.ONE_PASS_CREATED:
            case UIOrchestratorState.ONE_PASS_DELETED:
            case UIOrchestratorState.ONE_PASS_MODIFIED:
            case UIOrchestratorState.RECORDING_CREATED:
            case UIOrchestratorState.RECORDING_DELETED:
            case UIOrchestratorState.RECORDING_MODIFIED:
            case UIOrchestratorState.RECORDING_RECOVERED:
            case UIOrchestratorState.RECORDING_PERSISTENCE_MODIFIED:
                dispatch(
                    resetRecordingActivityState({
                        resetError: true,
                        resetCounter: true,
                    }),
                );
                break;
            default:
        }
    };

    UIOrchestrator.stateChangeListener.subscribe(callback);
    uiOrchestratorSubscription = callback;
};

export const unsubscribeFromUIOrchestratorRecordingActivity = () => {
    if (uiOrchestratorSubscription) {
        UIOrchestrator.stateChangeListener.cancel(uiOrchestratorSubscription);
        uiOrchestratorSubscription = null;
    }
};

const recordingActivityReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IRecordingActivityState>>{},
) => {
    switch (type) {
        case ON_SET_RECORDING_ACTIVITY_DATA:
            return { ...state, recordingItems: payload.recordingItems };
        case ON_SET_GHOST_MODE_RECORDING_ACTIVITY:
            return { ...state, ghostMode: payload.ghostMode };

        case ON_KEY_UP_RECORDING_ACTIVITY:
            return { ...state, verticalPosition: payload.verticalPosition };

        case ON_KEY_DOWN_RECORDING_ACTIVITY:
            return { ...state, verticalPosition: payload.verticalPosition };

        case ON_SET_VERTICAL_POSITION_RECORDING_ACTIVITY:
            return { ...state, verticalPosition: payload.verticalPosition };

        case ON_GO_NEXT_PAGE_RECORDING_ACTIVITY:
            return { ...state, currentPage: state.currentPage + 1, verticalPosition: 0 };

        case ON_GO_PREVIOUS_PAGE_RECORDING_ACTIVITY:
            return {
                ...state,
                currentPage: state.currentPage - 1,
                verticalPosition: payload.verticalPosition,
            };

        case ON_SET_CARET_VISIBILITY_RECORDING_ACTIVITY:
            return { ...state, isCaretVisible: payload.isCaretVisible };

        case ON_SET_TOTAL_COUNT_RECORDING_ACTIVITY:
            return { ...state, totalCount: payload.totalCount };

        case ON_SET_CURRENT_PAGE_RECORDING_ACTIVITY:
            return { ...state, currentPage: payload.currentPage };

        case ON_SET_RECORDING_ACTIVITY_SELECTED_ROW_PREVIEW:
            return {
                ...state,
                selectedRecordingActivityPreviewPromise:
                    payload.selectedRecordingActivityPreviewPromise,
            };

        case ON_SET_RECORDING_ACTIVITY_API_ERROR:
            return {
                ...state,
                isError: payload.isError,
            };
        case ON_RETRY_FETCH_RECORDING_ACTIVITY_WHEN_ERROR:
            return {
                ...state,
                retryFetchCounter: payload.retryFetchCounter,
            };
        default:
            return state;
    }
};

export const refreshCaretVisibility = () => (dispatch: Function, getState: Function) => {
    const { activeComponent } = getState().interactiveLayer;
    const { currentPage, totalCount } = getState().recordingActivity;

    if (activeComponent === InteractiveComponents.NORTH) {
        dispatch(setCaretVisibilityAction(currentPage > 0));
    } else if (activeComponent === InteractiveComponents.SOUTH) {
        const finalPage = Math.ceil(totalCount / LIST_ROWS_ON_PAGE) - 1;
        const isOnLastPage = currentPage >= finalPage || currentPage >= HARD_FINAL_PAGE;
        dispatch(setCaretVisibilityAction(!isOnLastPage));
    }
};

export const enableGhostModeRecordingActivity = () => (dispatch: Function) => {
    dispatch(setGhostModeAction(true));
    dispatch(refreshCaretVisibility());
};

export const disableGhostModeRecordingActivity = () => (dispatch: Function) => {
    dispatch(setGhostModeAction(false));
};

export const resetRecordingActivityState =
    ({ resetError = false, resetCounter = false }: IResetOptions = {}) =>
    (dispatch: Function) => {
        dispatch(setVerticalPositionRecordingActivityAction(0));
        dispatch(setGhostModeAction(false));
        dispatch(setRecordingActivityDataAction([]));
        dispatch(setCurrentPageRecordingActivityAction(0));
        dispatch(setTotalCountRecordingActivityAction(0));
        dispatch(setRecordingActivitySelectedRowPreviewAction(null));
        if (resetError) {
            dispatch(setRecordingActivityApiErrorAction(false));
        }
        if (resetCounter) {
            dispatch(setRetryFetchCounterAction(0));
        }
    };

export const handleRecordingActivityErrorOrNetwork = (dispatch: Function, isError: boolean) => {
    if (NetworkManager.isNetworkDisconnected()) {
        dispatch(
            showWhisper({
                message: NetworkManager.getNetworkDisconnectedMessage(),
                iconType: WhisperTypes.NO_ICON,
            }),
        );
        return true;
    }
    if (isError) {
        dispatch(onIncrementRetryFetchCounter());
        return true;
    }
    return false;
};

export const onIncrementRetryFetchCounter = () => (dispatch: Function, getState: Function) => {
    const { retryFetchCounter } = getState().recordingActivity;
    dispatch(setRetryFetchCounterAction(retryFetchCounter + 1));
};

export const showHistoryConflictOverlay =
    (currentItem: HistoryOfferItem) => (dispatch: Function, getState: Function) => {
        const { isActive: isAntiBurnActive } = getState().antiburnOsd;
        if (isAntiBurnActive) return;

        const { isOverlayDialogActive } = getState().overlayDialog;
        if (isOverlayDialogActive) dispatch(hideOverlayDialog());

        const services = new ServicesClientImpl();
        const shouldHideMetadata = services.shouldHideMetadata();
        const { title, description } = mapHistoryItemToMessageKey(currentItem);
        const options =
            currentItem.shortReason === HistoryOfferItemShortReasonEnum.Canrecover
                ? [
                      { text: Locale.getString(LocaleKeys.OVERLAY_OPTION_OK_TEXT) },
                      {
                          text: Locale.getString(LocaleKeys.RECORDING_ACTIVITY_GO_TO_RECOVER),
                      },
                  ]
                : [{ text: Locale.getString(LocaleKeys.OVERLAY_OPTION_OK_TEXT) }];

        const overlayDialogProps: IOverlayDialog = {
            description,
            hasDimmer: true,
            hasSeperator: false,
            mouseTrapValue: HISTORY_INFO_MOUSE_TRAP,
            options,
            overlayType: OverlayTypes.VOID,
            title,
            subtitle:
                shouldHideMetadata && currentItem.isAdult
                    ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN)
                    : getRecordingOfferItemTitle(currentItem),
        };
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.HISTORY_CONFLICT_INFO_OVERLAY));
        dispatch(showOverlayDialog(overlayDialogProps));
    };

export const onKeyOkNoTaskRecordingActivity =
    (goToRecover: boolean = false) =>
    async (dispatch: Function, getState: Function) => {
        const {
            currentPage,
            recordingItems,
            verticalPosition,
            selectedRecordingActivityPreviewPromise,
        } = getState().recordingActivity;
        const { actualItems } = getHistoryItemsFromPage(recordingItems, currentPage);
        const selectedItem = actualItems?.[verticalPosition];

        if (!selectedItem) return;

        const currentOfferPreview: TOfferPreviewCustomData | null = await getCurrentOfferPreview(
            selectedItem,
            selectedRecordingActivityPreviewPromise,
        );

        if (!currentOfferPreview) {
            Log.e(
                TAG,
                'Failed to fetch recording preview for the selected Recording History item.',
            );
            return;
        }

        const isExplicitlyAdult = selectedItem.isAdult ?? false;
        const isContentPCViolated = new ServicesClientImpl().checkPCViolations({
            offerPreview: currentOfferPreview,
        });

        const goToProgramScreen = () => async () => {
            if (!selectedItem?.recordingId) {
                Log.e(TAG, 'No recordingId found for the selected Recording Activity item.');
                return;
            }
            subscribeToUIOrchestrator(dispatch);

            dispatch(
                programScreenTransition(
                    {
                        id: selectedItem.recordingId,
                        contentType: ContentType.RECORDING_ID,
                    },
                    showWhisper,
                    {
                        targetInitialProgramScreenOption: goToRecover
                            ? ContentScreenPresentationId.RECOVER_RECORDING_DETAIL
                            : undefined,
                    },
                ),
            );
        };

        if (isExplicitlyAdult && isContentPCViolated) {
            dispatch(showPCPinOverlayWithProgramRating(goToProgramScreen, isExplicitlyAdult));
        } else {
            dispatch(goToProgramScreen());
        }
    };

const getCurrentOfferPreview = async (
    item: HistoryOfferItem,
    programPreviewPromise: Promise<TOfferPreview>,
): Promise<TOfferPreviewCustomData | null> => {
    try {
        const preview = await programPreviewPromise;
        return preview.offerPreview;
    } catch {
        if (!item.recordingId) {
            return null;
        }
        const service = new ServicesClientImpl();
        const res = await service.fetchRecordingPreview({
            recordingId: item.recordingId,
            applicationFeatureArea: 'recordingActivity',
        });
        return res.offerPreview;
    }
};

export default recordingActivityReducer;
